import { useMemo } from "react";
import { Link } from "react-router-dom";
import { H3, H3Live, H3Spark } from "./h3";
import { fmtChange } from "../tokens";
import { useV2Global, useV2History, useV2LayerIndex } from "../useV2Data";

const fmtT = (n?: number | null) =>
  n == null ? "—" : n >= 1e12 ? `$${(n / 1e12).toFixed(2)}T` : n >= 1e9 ? `$${(n / 1e9).toFixed(1)}B` : `$${Math.round(n).toLocaleString()}`;
const fmtPct = (n?: number | null) => (n == null ? "—" : `${n.toFixed(1)}%`);

/* Onchain Intelligence strip — Figma 214:233 (1440: inline value·delta·spark,
   no cell dividers, no header link) / 205:75 (768: 4×2 grid + MARKET OVERVIEW
   cell) / 206:79 (390: 2×4 grid). Real supported metrics only; deltas from
   recorded observations (>=18h anchor); sparklines are the real recorded
   24h observation series — omitted when insufficient. */
export const OnchainIntelStrip = () => {
  const { data: g } = useV2Global();
  const { data: li } = useV2LayerIndex();
  const { data: hist } = useV2History(24, true);

  const pts: any[] = hist?.points || [];
  const anchorFor = (f: (p: any) => number | null | undefined) => {
    const a = pts.find((p) => f(p) != null);
    if (!a) return null;
    const ageH = (Date.now() - new Date(a.t).getTime()) / 3.6e6;
    return ageH >= 18 ? (f(a) as number) : null;
  };
  const seriesFor = (f: (p: any) => number | null | undefined): number[] =>
    pts.map(f).filter((v): v is number => v != null);

  const d = useMemo(() => {
    const pp = (f: (p: any) => number | null | undefined, latest?: number | null) => {
      const a = anchorFor(f);
      return a != null && latest != null ? `${latest - a >= 0 ? "+" : "−"}${Math.abs(latest - a).toFixed(2)}pp` : null;
    };
    const volAnchor = anchorFor((p) => p.volume);
    return {
      vol: volAnchor && g?.volume_24h_usd != null ? fmtChange(((g.volume_24h_usd - volAnchor) / volAnchor) * 100) : null,
      btc: pp((p) => p.btc_dominance, g?.btc_dominance),
      eth: pp((p) => p.eth_dominance, g?.eth_dominance),
      sol: pp((p) => p.sol_dominance, g?.sol_dominance),
      stab: pp((p) => p.stables, g?.stables_dominance_usdt_usdc),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pts, g]);

  type Cell = { k: string; name: string; val: string; touchVal?: string; delta: string | null; dc: string; spark: number[] | null };
  const up = (s: string | null | undefined) => !!s && s.startsWith("+");
  const liScore = li?.value ?? li?.score;
  const cells = [
    liScore != null && {
      k: "layer-index", name: "LAYER INDEX", val: `${liScore}`,
      touchVal: `${liScore} · ${String(li.label || "").toUpperCase()}`,
      delta: String(li.label || "").toUpperCase() || null, dc: H3.amber, spark: null,
    },
    g?.total_market_cap_usd != null && {
      k: "mcap", name: "TOTAL MARKET CAP", val: fmtT(g.total_market_cap_usd),
      delta: fmtChange(g.market_cap_change_24h_pct),
      dc: (g.market_cap_change_24h_pct ?? 0) >= 0 ? H3.green : H3.red,
      spark: seriesFor((p) => p.mcap),
    },
    g?.volume_24h_usd != null && {
      k: "volume", name: "24H VOLUME", val: fmtT(g.volume_24h_usd), delta: d.vol,
      dc: up(d.vol) ? H3.green : H3.red, spark: seriesFor((p) => p.volume),
    },
    g?.btc_dominance != null && {
      k: "btc-dom", name: "BTC DOMINANCE", val: fmtPct(g.btc_dominance), delta: d.btc,
      dc: up(d.btc) ? H3.green : H3.red, spark: seriesFor((p) => p.btc_dominance),
    },
    g?.eth_dominance != null && {
      k: "eth-dom", name: "ETH DOMINANCE", val: fmtPct(g.eth_dominance), delta: d.eth,
      dc: up(d.eth) ? H3.green : H3.red, spark: seriesFor((p) => p.eth_dominance),
    },
    g?.sol_dominance != null && {
      k: "sol-share", name: "SOL SHARE", val: fmtPct(g.sol_dominance), delta: d.sol,
      dc: up(d.sol) ? H3.green : H3.red, spark: seriesFor((p) => p.sol_dominance),
    },
    g?.stables_dominance_usdt_usdc != null && {
      k: "stables", name: (g.stables_label || "USDT + USDC").toUpperCase() + " SHARE", val: fmtPct(g.stables_dominance_usdt_usdc),
      delta: d.stab, dc: up(d.stab) ? H3.green : H3.red, spark: seriesFor((p) => p.stables),
    },
  ].filter(Boolean) as Cell[];

  if (!cells.length) return null;

  return (
    <div data-testid="h3-onchain-intel" className="rounded-[10px] xl:rounded-[6px] px-[16px] py-[14px] xl:py-[12px]"
      style={{ background: H3.card, border: `1px solid ${H3.border}` }}>
      <p className="nl-geist text-[14px] md:text-[15px] xl:text-[12px] min-[1440px]:text-[13px] leading-[19px] font-bold inline-flex items-center gap-[12px]"
        style={{ color: H3.cyan }}>
        ONCHAIN INTELLIGENCE <H3Live at={g?.updated_at} testid="h3-onchain-live" />
      </p>
      <div className="mt-[12px] xl:mt-[14px] grid grid-cols-2 md:grid-cols-4 gap-y-[16px] xl:flex xl:gap-y-0">
        {cells.map((c) => {
          const dark = c.delta ? c.dc : H3.gray;
          return (
            <div key={c.k} data-testid={`h3-onchain-${c.k}`} className="min-w-0 pr-[8px] xl:flex-1 xl:pr-[12px]">
              <p className="nl-mono text-[8px] leading-[12px] whitespace-nowrap" style={{ color: H3.gray }}>{c.name}</p>
              {/* 1440/1280: value · delta · spark inline */}
              <div className="hidden xl:flex items-center gap-[7px] mt-[4px]">
                <p className="nl-sg text-[17px] min-[1440px]:text-[19px] leading-[23px] font-bold whitespace-nowrap" style={{ color: H3.text }}>{c.val}</p>
                {c.delta && <span className="nl-mono text-[9px] leading-[13px] font-bold whitespace-nowrap" style={{ color: dark }}>{c.delta}</span>}
                {c.spark && c.spark.length >= 2 && <H3Spark vals={c.spark} color={dark} w={46} h={12} sw={1.4} />}
              </div>
              {/* 768/390: value (+spark), delta below */}
              <div className="xl:hidden mt-[3px]">
                <div className="flex items-center gap-[8px]">
                  <p className="nl-sg text-[17px] md:text-[18px] leading-[24px] font-bold whitespace-nowrap" style={{ color: H3.text }}>
                    {c.touchVal || c.val}
                  </p>
                  {c.spark && c.spark.length >= 2 && <H3Spark vals={c.spark} color={dark} w={38} h={8} sw={1.2} />}
                </div>
                {!c.touchVal && c.delta && (
                  <p className="nl-mono text-[9px] md:text-[10px] leading-[14px] mt-[2px] font-semibold" style={{ color: dark }}>{c.delta}</p>
                )}
              </div>
            </div>
          );
        })}
        {/* 768/390 masters: 8th cell = MARKET OVERVIEW / OPEN → */}
        <div className="xl:hidden min-w-0 pr-[8px]">
          <p className="nl-mono text-[8px] leading-[12px] whitespace-nowrap" style={{ color: H3.gray }}>MARKET OVERVIEW</p>
          <Link to="/markets" data-testid="h3-onchain-viewall-touch"
            className="nl-sg block text-[17px] md:text-[18px] leading-[24px] font-bold mt-[3px] hover:opacity-80 transition-opacity"
            style={{ color: H3.text }}>
            OPEN →
          </Link>
        </div>
      </div>
    </div>
  );
};
